import { type MouseEvent, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import useEvent from 'react-use-event-hook'

import { ClipboardDocumentIcon } from '@gto/icons-react/outline/clipboard-document'
import { ClipboardDocumentCheckIcon } from '@gto/icons-react/outline/clipboard-document-check'
import { IconButton } from '@gto/ui/icon-button'
import { Tooltip } from '@gto/ui/tooltip'

type CopyButtonProps = {
  text: string
}

export const CopyButton = ({ text }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = useEvent((event: MouseEvent) => {
    // A copy action must never trigger an ancestor's click handler — e.g. the
    // coupons table's DataGrid row-click, which would otherwise open the edit
    // dialog when the user only meant to copy the code.
    event.stopPropagation()
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true)
        if (timeoutRef.current != null) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => setCopied(false), 2000)
      })
      .catch(console.error)
  })

  return (
    <Tooltip content={copied ? <FormattedMessage id="coupons.copied" /> : 'Copy'}>
      <IconButton
        aria-label={`Copy ${text}`}
        size="small"
        onClick={handleCopy}
      >
        {copied ? (
          <ClipboardDocumentCheckIcon
            className="text-primary"
            height={18}
            width={18}
          />
        ) : (
          <ClipboardDocumentIcon
            height={18}
            width={18}
          />
        )}
      </IconButton>
    </Tooltip>
  )
}
